<template>
    <FormSection :application-id="referral.application" :showBackground="false" size="0">
        <template v-if="isLoading" #content>
            <div class="p-5 d-flex flex-column text-center text-primary">
                <i class="far fa-save fa-7x me-2 animated slow flash infinite"></i>
                <div class="big-text mt-1">
                    <b>Save Payer In Progress...</b>
                </div>
            </div>
        </template>
        <template #content v-if="!isLoading">
            <div class="p-2 pb-0">
                <div class="row">
                    <div class="col-12 col-md-4 offset-md-1">
                        <div class="form-group">
                            <label for="payer">
                                Payer
                                <span class="text-danger">*</span>
                            </label>
                            <TypeaheadInput
                                :getData="getPayors"
                                :value="initialValue"
                                v-on:valueChanged="selectedPayorChangedd"
                                :init-value="selectedPayor"
                                placeholder="Start Typing..."
                                :debounce="100"
                                :disabled="!isAddForm"
                                v-on:blurredInput="v$.payer.agencyPayorName.$touch"
                                :class="`${!v$.payer.agencyPayorName.$error ? '' : 'field-invalid'}`"
                            />
                            <span v-if="v$.payer.agencyPayorName.$error" class="field-invalid">Payer is required.</span>
                        </div>
                    </div>
                    <div class="col-12 col-md-3">
                        <div class="form-group">
                            <label for="effective-date">
                                Effective Date
                                <span class="text-danger">*</span>
                            </label>
                            <DatePicker :getDate="changeEffectiveDate">
                                <div class="input-group date datepickerInput">
                                    <input
                                        type="text"
                                        name="effective-date"
                                        class="form-control"
                                        :value="formatDateInput(payer.effectiveDate)"
                                        @change="handleDateChange"
                                        placeholder="MM/DD/YYYY"
                                        autocomplete="off"
                                        @blur="v$.payer.effectiveDate.$touch"
                                        :class="`${!v$.payer.effectiveDate.$error ? '' : 'field-invalid'}`"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                    <span v-if="v$.payer.effectiveDate.$error" class="field-invalid"
                                        >Effective Date is required.</span
                                    >
                                </div>
                            </DatePicker>
                        </div>
                    </div>
                    <div class="col-12 col-md-3">
                        <div class="form-group">
                            <label for="payer-type">
                                Payer Type
                                <span class="text-danger">*</span>
                            </label>
                            <select
                                class="form-select"
                                name="payer-type"
                                v-model="payer.type"
                                @change="payerTypeUpdated()"
                                :disabled="!isAddForm"
                                @blur="v$.payer.type.$touch"
                                :class="`${!v$.payer.type.$error ? '' : 'field-invalid'}`"
                            >
                                <option :value="undefined">Select Payer Type</option>
                                <option v-for="[key, value] of enumToMap(payorTypeEnum)" :value="value.value">
                                    {{ value.name }}
                                </option>
                            </select>
                            <span v-if="v$.payer.type.$error" class="field-invalid">Type is required.</span>
                        </div>
                    </div>
                    <div class="col-12 col-md-10 offset-md-1" v-if="showRoomAndBoardWarning && !payer.isCharity">
                        <div class="alert alert-warning mb-2" role="alert">
                            <i class="fas fa-exclamation-triangle text-warning me-2"></i> This payer is not set up as a
                            room and board payer.
                            <a href="/list/payer-edit/${selectedPayor.value}/billing-information" target="_blank"
                                >Click here</a
                            >
                            to update the payer’s setup to include room & board billing.
                        </div>
                    </div>
                </div>
                <div v-if="payer.agencyPayorId && !payer.isCharity">
                    <div v-if="!payer.isMedicare">
                        <NonMedicarePayor
                            :payer="payer"
                            :relationshipToInsuredEnum="relationshipToInsuredEnum"
                            :payerSequenceCodeEnum="payerSequenceCodeEnum"
                            :hasDeductibleMetChanged="hasDeductibleMetChanged"
                            :isApplicationPalliative="isApplicationPalliative"
                            :showSubscriberName="showSubscriberName"
                            :roomAndBoardTypeValue="roomAndBoardTypeValue"
                            :requireAuthorizationChanged="requireAuthorizationChanged"
                            :outOfPocketMaxMetChanged="outOfPocketMaxMetChanged"
                        />
                    </div>
                    <div class="row w-100" v-if="payer.isMedicare">
                        <MedicarePayor
                            :payer="payer"
                            :isApplicationPalliative="isApplicationPalliative"
                            :requireAuthorizationChanged="requireAuthorizationChanged"
                            :outOfPocketMaxMetChanged="outOfPocketMaxMetChanged"
                            :getDate="medicareDateChange"
                        />
                    </div>
                    <div class="col-12 row form-group" v-if="isApplicationPalliative">
                        <div class="col-md-10 offset-md-1 px-0">
                            <label for="comments"> Comments </label>
                            <textarea
                                class="form-control"
                                id="comments"
                                v-model="payer.comments"
                                rows="4"
                                maxlength="2000"
                            ></textarea>
                        </div>
                        <div class="col-md-10 offset-md-1 px-0">
                            <div class="form-check custom-checkbox mt-2">
                                <input
                                    type="checkbox"
                                    :id="`benefits-verified_${payer.agencyId}`"
                                    class="form-check-input"
                                    v-model="payer.isBenefitVerified"
                                />
                                <label class="form-check-label" :for="`benefits-verified_${payer.agencyId}`">
                                    <span class="me-1">Benefits Verified</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./PayorForm.ts"></script>
